import React, { useState } from "react";
import { FolderPlus } from "lucide-react";
import { useForm } from "react-hook-form";
import { clsx } from "clsx";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useNavigate } from "react-router";
import { useCreateFolderMutation } from "@/api/files";
import { toast } from "react-toastify";
import * as analytics from "@/services/analytics";

const specialCharacters = /[!@#$%^&*()+=[\]{};':"\\|,.<>/?]+/;

interface CreateFolderModalProps {
  currentFolderId: string;
  isReadOnly?: boolean;
}

interface FormInputs {
  folderName: string;
}

const CreateFolderModal: React.FC<CreateFolderModalProps> = ({ currentFolderId, isReadOnly }) => {
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();

  const [createFolder] = useCreateFolderMutation();

  const methods = useForm<FormInputs>();

  const onSubmit = async (payload: FormInputs) => {
    try {
      const result = await createFolder({ name: payload.folderName, parent_id: currentFolderId }).unwrap();

      analytics.folderCreate(payload.folderName);
      setIsOpen(false);

      toast.success(`Folder "${payload.folderName}" has been created successfully.`);
      navigate(`/drive/${result.id}`);
    } catch (error) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      if ((error as any)?.data?.err_type === "FsEntryIsNotAFolderError") {
        methods.setError("folderName", {
          message: "This folder already exists, please create a folder with a unique name",
        });
      }
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button variant="second-white" disabled={isReadOnly} onClick={() => setIsOpen(true)}>
          <FolderPlus className="mr-2 h-5 w-5" />
          Create new folder
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[425px]">
        <form onSubmit={methods.handleSubmit(onSubmit)}>
          <DialogHeader>
            <DialogTitle>Create New Folder</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center">
              <Label htmlFor="folderName" className="pr-4 text-right">
                Name
              </Label>
              <div className="col-span-3">
                <Input
                  {...methods.register("folderName", {
                    required: "This is required.",
                    validate: value => !specialCharacters.test(value) || "Folder name contains special characters.",
                  })}
                  className={clsx({
                    "border-red-500": methods.formState.errors.folderName,
                  })}
                />
              </div>
              <Label />
              {!!methods.formState.errors.folderName && (
                <div className="col-span-3">
                  <p className="mt-2 text-sm text-red-500">{methods.formState.errors.folderName.message}</p>
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="outline" onClick={() => setIsOpen(false)}>
                Cancel
              </Button>
            </DialogClose>
            <Button type="submit" disabled={methods.formState.isSubmitting}>
              {methods.formState.isSubmitting ? "Creating..." : "Create"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CreateFolderModal;
